'use client';
import React, { useState, useMemo } from 'react';
import { Search, Filter, ChevronDown, Award, TrendingUp } from 'lucide-react';
import { DISTRIBUTORS, Rank, Distributor, QualStage } from '@/data/mockData';

import QualificationCard from './QualificationCard';
import RankHistoryTable from './RankHistoryTable';

const STAGES: { key: QualStage | 'All'; label: string; color: string }[] = [
  { key: 'All', label: 'All', color: 'text-zinc-400' },
  { key: 'None', label: 'Distributor', color: 'text-zinc-500' },
  { key: 'LOI', label: 'LOI — Month 1', color: 'text-amber-400' },
  { key: 'Q1', label: 'Q1 — Month 2', color: 'text-blue-400' },
  { key: 'Q2', label: 'Q2 — Month 3', color: 'text-emerald-400' },
  { key: 'Qualified', label: 'Executive', color: 'text-purple-400' },
];

const RANKS: Rank[] = ['Distributor', 'Brand Partner Gold', 'Brand Partner Lapis', 'Brand Partner Ruby', 'Executive', 'Provisional Executive', 'Emerald', 'Diamond', 'Blue Diamond', 'Presidential Director'];

export default function RankTrackerClient() {
  const [search, setSearch] = useState('');
  const [stageFilter, setStageFilter] = useState<QualStage | 'All'>('All');
  const [rankFilter, setRankFilter] = useState('All');
  const [activeFilter, setActiveFilter] = useState<'all' | 'active' | 'inactive'>('all');
  const [showRankDrop, setShowRankDrop] = useState(false);
  const [activeTab, setActiveTab] = useState<'pipeline' | 'history'>('pipeline');

  const allDists = Object.values(DISTRIBUTORS);

  const filtered = useMemo(() => {
    return allDists.filter(d => {
      const ms = search === '' || d.name.toLowerCase().includes(search.toLowerCase()) || d.id.includes(search);
      const mstage = stageFilter === 'All' || d.qualStage === stageFilter;
      const mr = rankFilter === 'All' || d.rank === rankFilter;
      const ma = activeFilter === 'all' || (activeFilter === 'active' ? d.active : !d.active);
      return ms && mstage && mr && ma;
    });
  }, [allDists, search, stageFilter, rankFilter, activeFilter]);

  // Group by stage for kanban
  const byStage = useMemo(() => {
    const stages: QualStage[] = ['None', 'LOI', 'Q1', 'Q2', 'Qualified'];
    return stages.reduce((acc, stage) => {
      acc[stage] = filtered.filter(d => d.qualStage === stage);
      return acc;
    }, {} as Record<QualStage, Distributor[]>);
  }, [filtered]);

  const stageConfig: Record<QualStage, { label: string; color: string; bg: string; gsvReq: number }> = {
    None: { label: 'Distributor', color: 'text-zinc-400', bg: 'border-zinc-700', gsvReq: 0 },
    LOI: { label: 'LOI — Month 1', color: 'text-amber-400', bg: 'border-amber-500/30', gsvReq: 1000 },
    Q1: { label: 'Q1 — Month 2', color: 'text-blue-400', bg: 'border-blue-500/30', gsvReq: 1500 },
    Q2: { label: 'Q2 — Month 3', color: 'text-emerald-400', bg: 'border-emerald-500/30', gsvReq: 2000 },
    Qualified: { label: 'Executive', color: 'text-purple-400', bg: 'border-purple-500/30', gsvReq: 2000 },
  };

  // Summary stats
  const qualifyingCount = allDists.filter(d => ['LOI', 'Q1', 'Q2'].includes(d.qualStage)).length;
  const pexecCount = allDists.filter(d => d.rank === 'Provisional Executive').length;
  const execCount = allDists.filter(d => ['Executive', 'Emerald', 'Diamond', 'Blue Diamond', 'Presidential Director'].includes(d.rank)).length;
  const atRiskCount = allDists.filter(d => d.active && d.psv < 100).length;

  return (
    <div className="flex flex-col h-full bg-zinc-950">
      {/* Header */}
      <div className="flex-shrink-0 px-6 py-4 border-b border-zinc-800 bg-zinc-900/40">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-lg font-bold text-zinc-100">Rank & Title Qualification Tracker</h1>
            <p className="text-xs text-zinc-500 mt-0.5">Monitor qualification pipeline, advancement progress, and PEXEC status across the network</p>
          </div>
          <div className="flex items-center gap-3">
            {[
              { label: 'Qualifying Now', value: qualifyingCount, color: 'text-amber-400', bg: 'bg-amber-500/10 border-amber-500/20' },
              { label: 'Active Execs', value: execCount, color: 'text-purple-400', bg: 'bg-purple-500/10 border-purple-500/20' },
              { label: 'PEXEC At-Risk', value: pexecCount, color: 'text-orange-400', bg: 'bg-orange-500/10 border-orange-500/20' },
              { label: 'Low PSV', value: atRiskCount, color: 'text-red-400', bg: 'bg-red-500/10 border-red-500/20' },
            ].map(s => (
              <div key={`rtstat-${s.label}`} className={`flex flex-col items-center px-3 py-1.5 rounded-xl border ${s.bg}`}>
                <span className={`text-xl font-bold font-mono ${s.color}`}>{s.value}</span>
                <span className="text-[9px] text-zinc-600 font-semibold uppercase tracking-wider mt-0.5">{s.label}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto scrollbar-thin">
        <div className="max-w-screen-2xl mx-auto px-6 py-5 space-y-5">
          {/* Filters */}
          <div className="flex items-center gap-3 flex-wrap">
            <div className="flex items-center gap-2 bg-zinc-800/60 border border-zinc-700 rounded-lg px-3 py-1.5 w-52">
              <Search size={14} className="text-zinc-500" />
              <input
                type="text"
                placeholder="Search distributor..."
                value={search}
                onChange={e => setSearch(e.target.value)}
                className="bg-transparent text-sm text-zinc-200 placeholder-zinc-600 outline-none w-full"
              />
            </div>

            {/* Stage filter chips */}
            <div className="flex items-center gap-1 flex-wrap">
              {STAGES.map(s => (
                <button
                  key={`sf-${s.key}`}
                  onClick={() => setStageFilter(s.key)}
                  className={`px-3 py-1 rounded-full text-xs font-semibold border transition-all ${
                    stageFilter === s.key
                      ? 'bg-violet-600 text-white border-violet-500'
                      : 'border-zinc-700 text-zinc-500 hover:text-zinc-300 hover:border-zinc-600'
                  }`}
                >
                  {s.label}
                </button>
              ))}
            </div>

            <div className="relative ml-auto">
              <button
                onClick={() => setShowRankDrop(!showRankDrop)}
                className="flex items-center gap-2 bg-zinc-800/60 border border-zinc-700 rounded-lg px-3 py-1.5 text-sm text-zinc-300"
              >
                <Filter size={13} className="text-zinc-500" />
                <span>{rankFilter === 'All' ? 'All Ranks' : rankFilter}</span>
                <ChevronDown size={13} className="text-zinc-500" />
              </button>
              {showRankDrop && (
                <div className="absolute right-0 top-full mt-1 z-50 bg-zinc-800 border border-zinc-700 rounded-lg shadow-xl py-1 w-52 animate-fade-in">
                  {['All', ...RANKS].map(r => (
                    <button
                      key={`rtrf-${r}`}
                      onClick={() => { setRankFilter(r); setShowRankDrop(false); }}
                      className={`w-full text-left px-3 py-1.5 text-xs hover:bg-zinc-700 transition-colors ${rankFilter === r ? 'text-violet-300' : 'text-zinc-300'}`}
                    >
                      {r}
                    </button>
                  ))}
                </div>
              )}
            </div>

            <div className="flex items-center gap-1 bg-zinc-800/40 border border-zinc-700 rounded-lg p-0.5">
              {(['all', 'active', 'inactive'] as const).map(f => (
                <button
                  key={`rtaf-${f}`}
                  onClick={() => setActiveFilter(f)}
                  className={`px-3 py-1 rounded-md text-xs font-medium transition-all ${activeFilter === f ? 'bg-violet-600 text-white' : 'text-zinc-400 hover:text-zinc-200'}`}
                >
                  {f.charAt(0).toUpperCase() + f.slice(1)}
                </button>
              ))}
            </div>
          </div>

          {/* Tabs */}
          <div className="border-b border-zinc-800">
            <div className="flex items-center gap-1">
              {[
                { key: 'pipeline', label: 'Qualification Pipeline', icon: Award },
                { key: 'history', label: 'Rank Advancement History', icon: TrendingUp },
              ].map(tab => (
                <button
                  key={`rttab-${tab.key}`}
                  onClick={() => setActiveTab(tab.key as any)}
                  className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium border-b-2 transition-all ${
                    activeTab === tab.key ? 'border-violet-500 text-violet-300' : 'border-transparent text-zinc-500 hover:text-zinc-300'
                  }`}
                >
                  <tab.icon size={14} />
                  {tab.label}
                </button>
              ))}
            </div>
          </div>

          {activeTab === 'pipeline' && (
            <div className="grid grid-cols-5 gap-3 items-start">
              {(['None', 'LOI', 'Q1', 'Q2', 'Qualified'] as QualStage[]).map(stage => {
                const cfg = stageConfig[stage];
                const members = byStage[stage] || [];
                return (
                  <div key={`kanban-${stage}`} className={`bg-zinc-900/60 border ${cfg.bg} rounded-2xl overflow-hidden`}>
                    <div className="px-3 py-2.5 border-b border-zinc-800/60 flex items-center justify-between">
                      <span className={`text-xs font-bold ${cfg.color}`}>{cfg.label}</span>
                      <span className="bg-zinc-800 text-zinc-400 text-[10px] font-bold rounded-full px-2 py-0.5">{members.length}</span>
                    </div>
                    {cfg.gsvReq > 0 && (
                      <div className="px-3 py-1.5 bg-zinc-800/30 border-b border-zinc-800/40">
                        <p className="text-[9px] text-zinc-600">Requires: 100 PSV + ADR + {(cfg.gsvReq / 1000).toFixed(1)}K GSV</p>
                      </div>
                    )}
                    <div className="p-2 space-y-2 min-h-[120px]">
                      {members.map(d => (
                        <QualificationCard key={`qcard-${d.id}`} distributor={d} stage={stage} gsvReq={cfg.gsvReq} />
                      ))}
                      {members.length === 0 && (
                        <div className="flex items-center justify-center py-6">
                          <p className="text-[10px] text-zinc-700">No distributors</p>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {activeTab === 'history' && <RankHistoryTable />}
        </div>
      </div>
    </div>
  );
}